import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import type * as React from 'react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import {
  AuditEventList,
  type AuditEventRow,
} from '../components/audit/AuditEventList';
import { orderEventLabel } from '../components/audit/event-labels';
import {
  ORDER_STATUS_VALUES,
  type StatusFilter,
} from './_authenticated.orders.index';

// Generic UUID shape. We don't enforce v4 specifically — any UUID-shaped
// string is accepted at the route level so this can be tightened (or
// loosened) later without a schema dance.
export const ORDER_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isOrderIdParam(value: unknown): value is string {
  return typeof value === 'string' && ORDER_ID_UUID_RE.test(value);
}

// Route param is the order UUID (orders.id). Per Decision 8,
// pharmacy_order_number is display-only and must never appear in URLs.
// parseParams runs before component mount: a non-UUID path segment
// (e.g. /orders/not-a-uuid) throws notFound() so the route-level
// notFoundComponent renders the same "Order not found" UI used for a
// valid-but-missing UUID. Avoids a Postgres "invalid input syntax for
// type uuid" error path that previously rendered a different state.
export const Route = createFileRoute('/_authenticated/orders/$orderId')({
  component: OrderDetailPage,
  notFoundComponent: OrderNotFoundRoute,
  parseParams: (params): { orderId: string } => {
    if (!isOrderIdParam(params.orderId)) {
      throw notFound();
    }
    return { orderId: params.orderId };
  },
  validateSearch: (search: Record<string, unknown>): { status: StatusFilter } => {
    const raw = search['status'];
    if (
      typeof raw === 'string' &&
      (raw === 'all' || (ORDER_STATUS_VALUES as readonly string[]).includes(raw))
    ) {
      return { status: raw as StatusFilter };
    }
    return { status: 'pending' };
  },
});

// ---------------------------------------------------------------------------
// Types — query shapes
// ---------------------------------------------------------------------------

type OrderDetailRow = {
  id: string;
  order_number: string;
  patient_id: string | null;
  patient_name: string | null;
  patient_phone: string | null;
  status: string;
  created_at: string;
  special_instructions: string | null;
  // Structured delivery preferences (PR #117). All default false.
  // Driver-app wiring is a follow-up; for now they render on the
  // pharmacy portal + audit PDF + Reports CSV.
  signature_required: boolean;
  adult_signature_required: boolean;
  leave_at_door: boolean;
  call_before_arrival: boolean;
  pharmacy_id: string;
  pharmacies: { id: string; name: string; city: string | null } | null;
  dropoff_stop: {
    id: string;
    sequence_number: number | null;
    address: string;
    city: string | null;
    state: string | null;
    zip: string | null;
    route_id: string | null;
    route: {
      id: string;
      planned_for: string;
      route_status: string;
      driver: { id: string; full_name: string | null; email: string } | null;
    } | null;
  } | null;
};

type PrescriptionRow = {
  id: string;
  rx_number: string | null;
  drug_name: string | null;
  drug_strength: string | null;
  dosage_form: string | null;
  quantity: number | null;
  refills_remaining: number | null;
  patient: {
    id: string;
    date_of_birth: string | null;
  } | null;
};

type DetailState =
  | { kind: 'loading' }
  | {
      kind: 'loaded';
      order: OrderDetailRow;
      prescriptions: PrescriptionRow[];
      routeStopCount: number | null;
    }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Matches the route detail page's planned_for formatting (PR #32).
// Parses YYYY-MM-DD as a local calendar date so negative-UTC offsets
// don't render the previous day.
function formatPlannedFor(iso: string): string {
  const parts = iso.split('-');
  if (parts.length === 3) {
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      const local = new Date(y, m - 1, d);
      return local.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  return iso;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const parts = iso.split('-');
  if (parts.length === 3) {
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  return iso;
}

function formatAddress(stop: OrderDetailRow['dropoff_stop']): string {
  if (!stop) return '—';
  const cityStateZip = [stop.city, stop.state].filter(Boolean).join(', ');
  const withZip = [cityStateZip, stop.zip].filter(Boolean).join(' ');
  return [stop.address, withZip].filter(Boolean).join(' · ');
}

// Structured delivery preferences → human-readable summary. Order
// matters here: adult signature is a superset of signature-required
// (regulators consider it stricter), so listing it first + skipping
// the plain-signature label when adult is true avoids visual noise.
// Leave-at-door is mutually exclusive with either signature flag —
// UI enforcement lives in the editor when we ship it (v1 read-only).
function formatDeliveryPreferences(order: OrderDetailRow): string {
  const parts: string[] = [];
  if (order.adult_signature_required) parts.push('Adult signature required');
  else if (order.signature_required) parts.push('Signature required');
  if (order.leave_at_door) parts.push('Leave at door');
  if (order.call_before_arrival) parts.push('Call before arrival');
  return parts.length > 0 ? parts.join(' · ') : '—';
}

// LYNRX_* error mapper for every order write path. Single function so
// the prefix → copy table lives in one place.
function friendlyOrderError(message: string | undefined): string {
  const m = message ?? '';
  if (m.includes('LYNRX_ORDER_NOT_FOUND'))
    return 'Order no longer accessible. Refresh.';
  if (m.includes('LYNRX_ORDER_NOT_CANCELLABLE'))
    return "This order can't be cancelled in its current state.";
  if (m.includes('LYNRX_ORDER_NOT_ELIGIBLE_FOR_RETRIEVAL'))
    return 'Only attempted or failed orders can be marked for retrieval.';
  if (m.includes('LYNRX_INVALID_REASON'))
    return 'Pick a valid cancel reason.';
  if (m.includes('LYNRX_ENTITY_NOT_FOUND'))
    return 'Entity no longer accessible. Refresh.';
  if (m.includes('LYNRX_INVALID_ENTITY_TYPE'))
    return 'Invalid entity type for PHI logging.';
  return 'Action failed. Try again.';
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function OrderDetailPage(): JSX.Element {
  const { orderId } = Route.useParams();
  const { status: backStatus } = Route.useSearch();
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    void navigate({ to: '/orders', search: { status: backStatus } });
  }, [navigate, backStatus]);
  return (
    <OrderDetailView orderId={orderId} backStatus={backStatus} onBack={goBack} />
  );
}

type ViewProps = {
  orderId: string;
  backStatus: StatusFilter;
  onBack: () => void;
  client?: LynrxSupabaseClient;
};

export function OrderDetailView({
  orderId,
  backStatus,
  onBack,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  const [state, setState] = useState<DetailState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  const title =
    state.kind === 'loaded' ? state.order.order_number : 'Order';
  useSetPageHeader({
    title,
    parentLabel: 'Orders',
    parentTo: '/orders',
  });

  // DEV-only demo mode — matches /exceptions?demo=1 and /reports?demo=1.
  // Serves a hardcoded high-touch order (CII controlled with adult
  // signature + call-before) so the Delivery preferences DlItem shows
  // real values without needing a seeded local DB or the prod migration
  // to have been applied. Tree-shakes via import.meta.env.DEV.
  const demoMode =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('demo') === '1';

  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    setState({ kind: 'loading' });

    if (demoMode) {
      setState({
        kind: 'loaded',
        order: DEMO_ORDER,
        prescriptions: DEMO_PRESCRIPTIONS,
        routeStopCount: null,
      });
      return;
    }

    void (async () => {
      // RLS scopes the queries to the caller's tenant. The order
      // select pulls the joined dropoff_stop + its route + assigned
      // driver. The prescription select is best-effort (the table is
      // largely empty at MVP per the schema docs; medication name +
      // RX number populate when a PMS integration writes prescriptions
      // ahead of order creation).
      const orderQuery = client
        .from('orders')
        .select(
          [
            'id',
            'order_number',
            'patient_id',
            'patient_name',
            'patient_phone',
            'status',
            'created_at',
            'special_instructions',
            'signature_required',
            'adult_signature_required',
            'leave_at_door',
            'call_before_arrival',
            'pharmacy_id',
            'pharmacies:pharmacies!orders_pharmacy_id_fkey(id, name, city)',
            // The dropoff stop carries the delivery address (server-side
            // truth for "where is this order going") AND links to the
            // route + driver. The driver join goes users→drivers; users
            // carries full_name + email so we render a human label.
            'dropoff_stop:stops!orders_dropoff_stop_id_fkey(id, sequence_number, address, city, state, zip, route_id, route:routes!stops_route_id_fkey(id, planned_for, route_status, driver:users!routes_driver_id_fkey(id, full_name, email)))',
          ].join(', '),
        )
        .eq('id', orderId)
        .is('deleted_at', null)
        .maybeSingle();

      const prescriptionsQuery = client
        .from('prescriptions')
        .select(
          'id, rx_number, drug_name, drug_strength, dosage_form, quantity, refills_remaining, patient:patients!prescriptions_patient_id_fkey(id, date_of_birth)',
        )
        .eq('order_id', orderId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });

      const [orderResult, presResult] = await Promise.all([
        orderQuery,
        prescriptionsQuery,
      ]);

      if (cancelled) return;

      if (orderResult.error) {
        captureSupabaseError(orderResult.error, 'orders.detail.fetch_order');
        setState({ kind: 'error', message: 'Could not load this order.' });
        return;
      }
      if (!orderResult.data) {
        setState({ kind: 'not_found' });
        return;
      }
      const order = orderResult.data as unknown as OrderDetailRow;

      // Prescriptions are non-fatal: order still renders if the join
      // fails. At MVP most orders have zero prescriptions.
      const prescriptions = presResult.error
        ? []
        : ((presResult.data ?? []) as unknown as PrescriptionRow[]);
      if (presResult.error) {
        captureSupabaseError(
          presResult.error,
          'orders.detail.fetch_prescriptions',
        );
      }

      // Route stop count — only fetched when the order is on a route,
      // so we can render "Stop X of Y". Lightweight count query.
      let routeStopCount: number | null = null;
      if (order.dropoff_stop?.route_id) {
        const stopsCount = await client
          .from('stops')
          .select('id', { count: 'exact', head: true })
          .eq('route_id', order.dropoff_stop.route_id);
        if (!cancelled && !stopsCount.error) {
          routeStopCount = stopsCount.count ?? null;
        }
        if (stopsCount.error) {
          captureSupabaseError(
            stopsCount.error,
            'orders.detail.fetch_route_stop_count',
          );
        }
      }

      if (cancelled) return;
      setState({ kind: 'loaded', order, prescriptions, routeStopCount });
    })();

    return () => {
      cancelled = true;
    };
  }, [client, session, isReady, orderId, reloadToken, demoMode]);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4">
        <Link
          to="/orders"
          search={{ status: backStatus }}
          className="text-sm text-primary hover:underline"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onBack();
          }}
        >
          ← Back to orders
        </Link>
      </div>

      {state.kind === 'loading' ? (
        <Card>
          <p className="text-sm text-ink-2">Loading order…</p>
        </Card>
      ) : null}

      {state.kind === 'not_found' ? <OrderNotFoundCard onBack={onBack} /> : null}

      {state.kind === 'error' ? (
        <Alert tone="error" title="Something went wrong">
          {state.message}
        </Alert>
      ) : null}

      {state.kind === 'loaded' ? (
        <LoadedView
          order={state.order}
          prescriptions={state.prescriptions}
          routeStopCount={state.routeStopCount}
          client={client}
          onRefresh={refresh}
          demoMode={demoMode}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LoadedView — orchestrates the five panels
// ---------------------------------------------------------------------------

function LoadedView({
  order,
  prescriptions,
  routeStopCount,
  client,
  onRefresh,
  demoMode,
}: {
  order: OrderDetailRow;
  prescriptions: PrescriptionRow[];
  routeStopCount: number | null;
  client: LynrxSupabaseClient;
  onRefresh: () => void;
  demoMode: boolean;
}): JSX.Element {
  // Status is lifted into local state so cancel/retrieval transitions
  // can update the badge + button visibility optimistically before
  // the RPC returns.
  const [currentStatus, setCurrentStatus] = useState<string>(order.status);
  useEffect(() => setCurrentStatus(order.status), [order.status]);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [retrievalOpen, setRetrievalOpen] = useState(false);

  return (
    <>
      <OrderHeader
        order={order}
        currentStatus={currentStatus}
        onCancelClick={() => setCancelOpen(true)}
        onRetrievalClick={() => setRetrievalOpen(true)}
      />

      <div className="mt-6 grid gap-6">
        <PatientPanel order={order} prescriptions={prescriptions} client={client} />
        <MedicationPanel
          order={order}
          prescriptions={prescriptions}
          client={client}
          onRefresh={onRefresh}
          demoMode={demoMode}
        />
        <RouteAssignmentPanel order={order} routeStopCount={routeStopCount} />
        <AuditTrailPanel orderId={order.id} client={client} />
      </div>

      <CancelOrderModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        orderId={order.id}
        client={client}
        onCancelled={(newStatus) => {
          setCurrentStatus(newStatus);
          setCancelOpen(false);
          onRefresh();
        }}
      />

      <MarkForRetrievalModal
        open={retrievalOpen}
        onClose={() => setRetrievalOpen(false)}
        orderId={order.id}
        client={client}
        onMarked={(newStatus) => {
          setCurrentStatus(newStatus);
          setRetrievalOpen(false);
          onRefresh();
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Panel: Header
// ---------------------------------------------------------------------------

const CANCEL_ELIGIBLE_STATUSES = new Set([
  'pending',
  'assigned',
  'out_for_delivery',
  'attempted',
  'failed',
]);
const RETRIEVAL_ELIGIBLE_STATUSES = new Set(['attempted', 'failed']);
// Audit PDF only makes sense once a delivery attempt has happened —
// pre-attempt orders have no POD, no geofence outcome, no delivery row.
const EXPORT_PDF_ELIGIBLE_STATUSES = new Set([
  'delivered',
  'attempted',
  'failed',
]);

function OrderHeader({
  order,
  currentStatus,
  onCancelClick,
  onRetrievalClick,
}: {
  order: OrderDetailRow;
  currentStatus: string;
  onCancelClick: () => void;
  onRetrievalClick: () => void;
}): JSX.Element {
  const canCancel = CANCEL_ELIGIBLE_STATUSES.has(currentStatus);
  const canRetrieve = RETRIEVAL_ELIGIBLE_STATUSES.has(currentStatus);
  const canExportPdf = EXPORT_PDF_ELIGIBLE_STATUSES.has(currentStatus);
  return (
    <header className="rounded-md border border-border bg-bg px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-medium text-ink">
            Order {order.order_number}
          </h1>
          <p className="mt-0.5 text-xs text-ink-2">
            Created {formatDateTime(order.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge entity="order" status={currentStatus} />
          {canExportPdf ? (
            <ExportAuditPdfButton
              orderId={order.id}
              orderNumber={order.order_number}
            />
          ) : null}
          {canRetrieve ? (
            <Button variant="secondary" onClick={onRetrievalClick}>
              Mark for retrieval
            </Button>
          ) : null}
          {canCancel ? (
            <button
              type="button"
              onClick={onCancelClick}
              className="inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel order
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Export audit PDF — POST /api/audit/pdf, download blob
// ---------------------------------------------------------------------------
// Visible only for delivered / attempted / failed statuses (set above).
// The endpoint is RLS-scoped: a cross-tenant probe gets 404, never PDF.

function ExportAuditPdfButton({
  orderId,
  orderNumber,
}: {
  orderId: string;
  orderNumber: string;
}): JSX.Element {
  const { session } = useAuth();
  const [pending, setPending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function onExportClick(): Promise<void> {
    if (!session) {
      setErrorMsg('Sign in expired. Refresh and try again.');
      return;
    }
    setPending(true);
    setErrorMsg(null);
    try {
      const res = await fetch('/api/audit/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      });
      if (!res.ok) {
        setErrorMsg(
          res.status === 404
            ? 'Order not available for export.'
            : 'PDF export failed. Try again.',
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeOrderNumber = orderNumber.replace(/[^A-Za-z0-9._-]/g, '_');
      const today = new Date().toISOString().slice(0, 10);
      a.download = `lynrx-pod-${safeOrderNumber}-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setErrorMsg('PDF export failed. Try again.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        onClick={() => void onExportClick()}
        disabled={pending}
      >
        {pending ? 'Exporting…' : 'Export audit PDF'}
      </Button>
      {errorMsg ? (
        <span role="alert" className="text-xs text-red-700">
          {errorMsg}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: Patient (with masked DOB + log_phi_access on reveal)
// ---------------------------------------------------------------------------

function PatientPanel({
  order,
  prescriptions,
  client,
}: {
  order: OrderDetailRow;
  prescriptions: PrescriptionRow[];
  client: LynrxSupabaseClient;
}): JSX.Element {
  // DOB comes from the first prescription's joined patient row, since
  // orders has no direct patient_id FK. At MVP most orders have zero
  // prescriptions → DOB is unavailable. The masked-field framework
  // is intact so the click-to-reveal compliance pattern works when
  // PMS data lands.
  const dob = prescriptions[0]?.patient?.date_of_birth ?? null;
  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Patient</h2>
      <Card>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <DlItem label="Name" value={order.patient_name ?? '—'} />
          <DlItem label="Phone" value={order.patient_phone ?? '—'} />
          <DlItem label="Address" value={formatAddress(order.dropoff_stop)} />
          <DlItem
            label="Date of birth"
            value={
              <MaskedPhi
                value={dob ? formatDate(dob) : '—'}
                hasValue={Boolean(dob)}
                field="patient_dob"
                entityType="order"
                entityId={order.id}
                client={client}
              />
            }
          />
        </dl>
        {order.patient_id ? (
          <p className="mt-3 text-sm">
            <Link
              to="/patients/$patientId"
              params={{ patientId: order.patient_id }}
              search={{}}
              className="text-primary hover:underline"
            >
              View patient record →
            </Link>
          </p>
        ) : null}
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Panel: Medication / order details
// ---------------------------------------------------------------------------

function MedicationPanel({
  order,
  prescriptions,
  client,
  onRefresh,
  demoMode,
}: {
  order: OrderDetailRow;
  prescriptions: PrescriptionRow[];
  client: LynrxSupabaseClient;
  onRefresh: () => void;
  demoMode: boolean;
}): JSX.Element {
  const firstRx = prescriptions[0] ?? null;
  const [prefsEditOpen, setPrefsEditOpen] = useState(false);
  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Medication &amp; order</h2>
      <Card>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <DlItem
            label="Medication"
            value={
              <MaskedPhi
                value={firstRx?.drug_name ?? '—'}
                hasValue={Boolean(firstRx?.drug_name)}
                field="medication_name"
                entityType="order"
                entityId={order.id}
                client={client}
              />
            }
          />
          <DlItem label="RX number" value={firstRx?.rx_number ?? '—'} />
          <DlItem
            label="Quantity"
            value={firstRx?.quantity != null ? String(firstRx.quantity) : '—'}
          />
          <DlItem label="Strength" value={firstRx?.drug_strength ?? '—'} />
          <DlItem
            label="Delivery instructions"
            value={
              order.special_instructions && order.special_instructions.length > 0
                ? order.special_instructions
                : '—'
            }
          />
          <DlItem
            label="Delivery preferences"
            value={
              <span className="inline-flex items-center gap-2">
                <span>{formatDeliveryPreferences(order)}</span>
                <button
                  type="button"
                  onClick={() => setPrefsEditOpen(true)}
                  className="text-[11px] font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  Edit
                </button>
              </span>
            }
          />
          <DlItem
            label="Pharmacy"
            value={
              order.pharmacies
                ? `${order.pharmacies.name}${order.pharmacies.city ? ` · ${order.pharmacies.city}` : ''}`
                : '—'
            }
          />
        </dl>
        {prescriptions.length === 0 ? (
          <p className="mt-3 text-xs italic text-ink-2">
            No prescription details on file. Medication name, RX number, and
            quantity populate when PMS data lands for this order.
          </p>
        ) : null}
      </Card>
      <DeliveryPreferencesModal
        open={prefsEditOpen}
        order={order}
        client={client}
        demoMode={demoMode}
        onClose={() => setPrefsEditOpen(false)}
        onSaved={() => {
          setPrefsEditOpen(false);
          onRefresh();
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Delivery preferences editor (PR #118)
// ---------------------------------------------------------------------------
// Modal-based editor for the four boolean columns PR #117 added to orders.
// Enforces the same server-side invariants the RPC does — client-side so
// the user gets an instant response, server-side so a stale client or a
// curl user can't write nonsense combinations:
//
//   - `leave_at_door` is mutually exclusive with any signature flag.
//     Checking leave_at_door disables + unchecks the signature toggles;
//     checking either signature toggle disables + unchecks leave_at_door.
//   - `adult_signature_required` implies `signature_required`. Checking
//     the adult toggle auto-checks signature and locks it on. Unchecking
//     adult leaves signature at whatever it was.

type DeliveryPreferencesForm = {
  signature_required: boolean;
  adult_signature_required: boolean;
  leave_at_door: boolean;
  call_before_arrival: boolean;
};

function DeliveryPreferencesModal({
  open,
  order,
  client,
  demoMode,
  onClose,
  onSaved,
}: {
  open: boolean;
  order: OrderDetailRow;
  client: LynrxSupabaseClient;
  demoMode: boolean;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [form, setForm] = useState<DeliveryPreferencesForm>(() => ({
    signature_required: order.signature_required,
    adult_signature_required: order.adult_signature_required,
    leave_at_door: order.leave_at_door,
    call_before_arrival: order.call_before_arrival,
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when the modal reopens with a (possibly changed) order.
  useEffect(() => {
    if (!open) return;
    setForm({
      signature_required: order.signature_required,
      adult_signature_required: order.adult_signature_required,
      leave_at_door: order.leave_at_door,
      call_before_arrival: order.call_before_arrival,
    });
    setError(null);
  }, [open, order]);

  const signaturePicked =
    form.signature_required || form.adult_signature_required;
  const leaveAtDoorDisabled = signaturePicked;
  const signatureDisabled = form.leave_at_door;

  const toggleSignature = (next: boolean): void => {
    setForm((prev) => ({
      ...prev,
      signature_required: next,
      // Turning off signature also turns off adult (adult implies
      // signature; can't have adult without signature).
      adult_signature_required: next ? prev.adult_signature_required : false,
    }));
  };

  const toggleAdultSignature = (next: boolean): void => {
    setForm((prev) => ({
      ...prev,
      adult_signature_required: next,
      // Adult ON forces signature ON; adult OFF leaves signature as-is.
      signature_required: next ? true : prev.signature_required,
    }));
  };

  const toggleLeaveAtDoor = (next: boolean): void => {
    setForm((prev) => ({
      ...prev,
      leave_at_door: next,
      // Turning on leave_at_door clears both signature flags — enforced
      // server-side too, but the UI clears them for clarity.
      signature_required: next ? false : prev.signature_required,
      adult_signature_required: next ? false : prev.adult_signature_required,
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (demoMode) {
      // Mirror the server-side write into the local order row so the
      // walkthrough shows the edit taking effect. Real RPC path
      // returns void; onSaved triggers a refresh which in demo mode
      // just re-uses DEMO_ORDER. Mutate the fixture in place so the
      // refresh reflects the new values.
      DEMO_ORDER.signature_required =
        form.signature_required || form.adult_signature_required;
      DEMO_ORDER.adult_signature_required = form.adult_signature_required;
      DEMO_ORDER.leave_at_door = form.leave_at_door;
      DEMO_ORDER.call_before_arrival = form.call_before_arrival;
      setSubmitting(false);
      onSaved();
      return;
    }

    const { error: rpcError } = await client.rpc(
      'set_order_delivery_preferences',
      {
        p_order_id: order.id,
        p_signature_required: form.signature_required,
        p_adult_signature_required: form.adult_signature_required,
        p_leave_at_door: form.leave_at_door,
        p_call_before_arrival: form.call_before_arrival,
      },
    );
    setSubmitting(false);
    if (rpcError) {
      captureSupabaseError(rpcError, 'orders.delivery_preferences.update');
      setError(
        rpcError.message.includes('LYNRX_INVALID_DELIVERY_PREFERENCES')
          ? 'Leave at door and signature required can\'t both be on. Pick one.'
          : rpcError.message.includes('LYNRX_FORBIDDEN')
            ? 'You don\'t have permission to edit this order.'
            : rpcError.message.includes('LYNRX_ORDER_NOT_FOUND')
              ? 'This order is no longer accessible. Refresh the page.'
              : 'Could not save. Please try again.',
      );
      return;
    }
    onSaved();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit delivery preferences"
      titleId="edit-delivery-prefs-title"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-delivery-prefs-form"
            loading={submitting}
            disabled={submitting}
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="edit-delivery-prefs-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        <p className="text-xs text-ink-2">
          Signature and leave-at-door are mutually exclusive. Adult signature
          (18+) implies signature.
        </p>
        {error ? <Alert tone="error">{error}</Alert> : null}
        <PrefCheckbox
          id="pref-signature"
          label="Signature required"
          hint="Any adult signature at handoff."
          checked={form.signature_required}
          onChange={toggleSignature}
          disabled={signatureDisabled || form.adult_signature_required}
        />
        <PrefCheckbox
          id="pref-adult-signature"
          label="Adult signature required (18+)"
          hint="Standard for DEA-controlled substances."
          checked={form.adult_signature_required}
          onChange={toggleAdultSignature}
          disabled={signatureDisabled}
        />
        <PrefCheckbox
          id="pref-leave-at-door"
          label="Leave at door"
          hint="No-contact delivery. Driver photographs the drop."
          checked={form.leave_at_door}
          onChange={toggleLeaveAtDoor}
          disabled={leaveAtDoorDisabled}
        />
        <PrefCheckbox
          id="pref-call-before"
          label="Call before arrival"
          hint="Driver phones the patient before attempting delivery."
          checked={form.call_before_arrival}
          onChange={(v) =>
            setForm((prev) => ({ ...prev, call_before_arrival: v }))
          }
          disabled={false}
        />
      </form>
    </Modal>
  );
}

function PrefCheckbox({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <label
      htmlFor={id}
      className={
        'flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 ' +
        (disabled
          ? 'cursor-not-allowed border-border bg-bg-soft opacity-60'
          : 'border-border bg-bg hover:border-ink-3')
      }
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border text-primary focus:ring-primary disabled:cursor-not-allowed"
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-ink">{label}</span>
        <span className="text-xs text-ink-2">{hint}</span>
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Panel: Route assignment
// ---------------------------------------------------------------------------

function RouteAssignmentPanel({
  order,
  routeStopCount,
}: {
  order: OrderDetailRow;
  routeStopCount: number | null;
}): JSX.Element {
  const stop = order.dropoff_stop;
  if (!stop?.route_id || !stop.route) {
    return (
      <section>
        <h2 className="mb-2 text-sm font-medium text-ink">Route assignment</h2>
        <Card>
          <p className="text-sm text-ink-2">
            Not yet assigned to a route.{' '}
            <Link
              to="/routes"
              search={{ date: 'all', status: 'all', driver: 'all' }}
              className="text-primary hover:underline"
            >
              Build or edit routes →
            </Link>
          </p>
        </Card>
      </section>
    );
  }
  const route = stop.route;
  const stopLabel =
    routeStopCount && stop.sequence_number
      ? `Stop ${stop.sequence_number} of ${routeStopCount}`
      : stop.sequence_number
        ? `Stop ${stop.sequence_number}`
        : null;
  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Route assignment</h2>
      <Card>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <DlItem
            label="Route"
            value={
              <Link
                to="/routes/$routeId"
                params={{ routeId: route.id }}
                className="text-primary hover:underline"
              >
                {formatPlannedFor(route.planned_for)}
              </Link>
            }
          />
          <DlItem
            label="Route status"
            value={<StatusBadge entity="route" status={route.route_status} />}
          />
          <DlItem label="Stop position" value={stopLabel ?? '—'} />
          <DlItem
            label="Driver"
            value={
              route.driver
                ? route.driver.full_name ?? route.driver.email
                : 'Unassigned'
            }
          />
        </dl>
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Panel: Audit trail
// ---------------------------------------------------------------------------

const AUDIT_PAGE_SIZE = 20;

// Event-label mapping was lifted into ../components/audit/event-labels.ts
// (orderEventLabel) so the audit PDF document can reuse the same strings.

function AuditTrailPanel({
  orderId,
  client,
}: {
  orderId: string;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<AuditEventRow[] | null>(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await client
        .from('audit_events')
        .select(
          'id, occurred_at, event_type, actor_user_id, actor_type, payload, actor_user:users!audit_events_actor_user_id_fkey(full_name, email)',
        )
        .eq('entity_type', 'order')
        .eq('entity_id', orderId)
        .order('occurred_at', { ascending: false })
        .range(page * AUDIT_PAGE_SIZE, page * AUDIT_PAGE_SIZE + AUDIT_PAGE_SIZE);
      if (cancelled) return;
      if (error) {
        captureSupabaseError(error, 'orders.detail.fetch_audit');
        setRows([]);
        setHasMore(false);
        return;
      }
      const all = (data ?? []) as unknown as AuditEventRow[];
      setHasMore(all.length > AUDIT_PAGE_SIZE);
      setRows(all.slice(0, AUDIT_PAGE_SIZE));
    })();
    return () => {
      cancelled = true;
    };
  }, [client, orderId, page]);

  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Activity</h2>
      {rows === null ? (
        <p className="text-sm text-ink-2">Loading activity…</p>
      ) : (
        <>
          <AuditEventList
            rows={rows}
            emptyMessage="No order-specific events yet."
            eventLabel={orderEventLabel}
          />
          <div className="mt-2 flex items-center justify-end gap-2 text-sm">
            <Button
              variant="ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Newer
            </Button>
            <span className="text-xs text-ink-2">Page {page + 1}</span>
            <Button
              variant="ghost"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Older →
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// MaskedPhi — click-to-reveal + log_phi_access call
// ---------------------------------------------------------------------------

function MaskedPhi({
  value,
  hasValue,
  field,
  entityType,
  entityId,
  client,
}: {
  value: string;
  hasValue: boolean;
  field: string;
  entityType: 'order' | 'patient';
  entityId: string;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [revealed, setRevealed] = useState(false);
  const [logging, setLogging] = useState(false);

  const onReveal = useCallback(async () => {
    if (revealed || !hasValue) {
      setRevealed(true);
      return;
    }
    setLogging(true);
    try {
      const { error } = await client.rpc('log_phi_access', {
        p_entity_type: entityType,
        p_entity_id:   entityId,
        p_field:       field,
      });
      if (error) {
        // Log the failure but proceed with reveal — the compliance
        // story degrades to "we have a Sentry record this fired but
        // not an audit row". Better than blocking the dispatcher
        // from doing their job on a transient RPC failure.
        captureSupabaseError(error, `orders.detail.log_phi_access.${field}`);
      }
    } catch (err) {
      captureSupabaseError(err, `orders.detail.log_phi_access.${field}.exception`);
    } finally {
      setLogging(false);
      setRevealed(true);
    }
  }, [client, entityId, entityType, field, hasValue, revealed]);

  if (!hasValue) {
    return <span className="text-ink-2">—</span>;
  }
  if (revealed) {
    return <span className="text-ink">{value}</span>;
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono text-ink-2">••••••</span>
      <button
        type="button"
        onClick={() => void onReveal()}
        disabled={logging}
        className="text-xs font-medium text-primary underline-offset-4 hover:underline disabled:opacity-60"
      >
        {logging ? 'Logging…' : 'Show'}
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Modal: Cancel order
// ---------------------------------------------------------------------------

const CANCEL_REASONS: { value: string; label: string }[] = [
  { value: 'customer_cancellation', label: 'Customer cancellation' },
  { value: 'duplicate',             label: 'Duplicate order' },
  { value: 'pharmacy_error',        label: 'Pharmacy error' },
  { value: 'other',                 label: 'Other' },
];

function CancelOrderModal({
  open,
  onClose,
  orderId,
  client,
  onCancelled,
}: {
  open: boolean;
  onClose: () => void;
  orderId: string;
  client: LynrxSupabaseClient;
  onCancelled: (newStatus: string) => void;
}): JSX.Element {
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setNotes('');
      setSubmitError(null);
    }
  }, [open]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!reason) {
        setSubmitError('Pick a reason.');
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const { error } = await client.rpc('cancel_order', {
          p_order_id: orderId,
          p_reason:   reason,
          // Regen drift after PR #72's types regen: cancel_order.p_notes
          // accepts null at runtime (the SQL function declares it
          // nullable; the "empty notes" path has shipped since the
          // bulk_action_rpcs migration), but the regenerated Args type
          // dropped the `| null` annotation. Cast keeps runtime
          // behavior; remove after the regen recovers the annotation.
          p_notes:    (notes.trim() === '' ? null : notes.trim()) as string,
        });
        if (error) {
          captureSupabaseError(error, 'orders.detail.cancel_order');
          setSubmitError(friendlyOrderError(error.message));
          return;
        }
        onCancelled('cancelled');
      } catch (err) {
        captureSupabaseError(err, 'orders.detail.cancel_order.exception');
        setSubmitError(friendlyOrderError(undefined));
      } finally {
        setSubmitting(false);
      }
    },
    [client, notes, onCancelled, orderId, reason],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancel order"
      titleId="cancel-order-title"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Keep order
          </Button>
          <Button
            type="submit"
            form="cancel-order-form"
            variant="primary"
            disabled={submitting || !reason}
          >
            {submitting ? 'Cancelling…' : 'Cancel order'}
          </Button>
        </>
      }
    >
      <form id="cancel-order-form" onSubmit={onSubmit} className="space-y-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink">Reason</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Cancel reason"
          >
            <option value="">— Select reason —</option>
            {CANCEL_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink">Notes (optional)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Anything that wouldn't be obvious from the reason alone."
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </label>
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal: Mark for retrieval
// ---------------------------------------------------------------------------

function MarkForRetrievalModal({
  open,
  onClose,
  orderId,
  client,
  onMarked,
}: {
  open: boolean;
  onClose: () => void;
  orderId: string;
  client: LynrxSupabaseClient;
  onMarked: (newStatus: string) => void;
}): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSubmitError(null);
  }, [open]);

  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { error } = await client.rpc('mark_order_for_retrieval', {
        p_order_id: orderId,
      });
      if (error) {
        captureSupabaseError(error, 'orders.detail.mark_for_retrieval');
        setSubmitError(friendlyOrderError(error.message));
        return;
      }
      onMarked('returned');
    } catch (err) {
      captureSupabaseError(err, 'orders.detail.mark_for_retrieval.exception');
      setSubmitError(friendlyOrderError(undefined));
    } finally {
      setSubmitting(false);
    }
  }, [client, onMarked, orderId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mark for retrieval"
      titleId="retrieval-title"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={submitting}
          >
            {submitting ? 'Marking…' : 'Mark for retrieval'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink">
        This will mark the order for retrieval. The order will need to be
        brought back to the pharmacy on the next available route. Continue?
      </p>
      {submitError ? (
        <div className="mt-3">
          <Alert tone="error">{submitError}</Alert>
        </div>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function DlItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-ink-2">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Not-found surfaces
// ---------------------------------------------------------------------------

export function OrderNotFoundCard({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <Alert tone="error" title="Order not found">
      We couldn&apos;t find an order with that ID.
      <div className="mt-3">
        <Button variant="secondary" onClick={onBack}>
          Back to orders
        </Button>
      </div>
    </Alert>
  );
}

function OrderNotFoundRoute(): JSX.Element {
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    void navigate({ to: '/orders', search: { status: 'pending' } });
  }, [navigate]);
  return (
    <main className="min-h-screen bg-bg-soft px-6 py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6">
          <Link
            to="/orders"
            search={{ status: 'pending' }}
            className="text-sm text-primary hover:underline"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              onBack();
            }}
          >
            ← Back to orders
          </Link>
        </div>
        <OrderNotFoundCard onBack={onBack} />
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// DEV-only demo fixtures. See handleFetch above for the ?demo=1 gate.
// Never used in prod — the demo mode is gated on `import.meta.env.DEV`
// and tree-shakes out. Names and Rx numbers are obviously fake so a
// screenshot can't be confused with real PHI.
// ---------------------------------------------------------------------------

const DEMO_ORDER: OrderDetailRow = {
  id: 'demo-order-1',
  order_number: 'RX-DEMO-0001',
  patient_id: null,
  patient_name: 'Kowalski, Ada',
  patient_phone: '(555) 010-1234',
  status: 'pending',
  created_at: new Date().toISOString(),
  special_instructions: 'Dog in the yard — please call from the driveway.',
  // High-touch delivery: CII controlled substance with adult signature
  // + call-before. Showcases all four preference fields lit up so the
  // formatDeliveryPreferences() output has real content.
  signature_required: false,
  adult_signature_required: true,
  leave_at_door: false,
  call_before_arrival: true,
  pharmacy_id: 'demo-pharmacy',
  pharmacies: { id: 'demo-pharmacy', name: 'Aultman Pharmacy', city: 'Canton' },
  dropoff_stop: {
    id: 'demo-stop',
    sequence_number: 4,
    address: '742 Evergreen Terrace',
    city: 'Canton',
    state: 'OH',
    zip: '44710',
    route_id: null,
    route: null,
  },
};

const DEMO_PRESCRIPTIONS: PrescriptionRow[] = [
  {
    id: 'demo-rx-1',
    rx_number: '4877318',
    drug_name: 'Oxycodone 10mg',
    drug_strength: '10mg',
    dosage_form: 'tablet',
    quantity: 30,
    refills_remaining: 0,
    patient: null,
  },
];
